<template>
  <div class="mobile-source-product">
    <section class="relative h-64 bg-cover bg-center"  :style="{ backgroundImage: `url(${sourceProduct})` }">
      <div class="absolute inset-0 bg-black bg-opacity-40"></div>
      <div class="relative z-10 h-full flex items-center px-4">
        <h1 class="text-white text-xl font-bold">
            {{ heroTitle }}
        </h1>
      </div>
    </section>


    <!-- Illustrations & Categories Section -->
    <section class="py-6 bg-white w-full">
      <div class="mx-4">
        <h2 class="text-lg font-bold text-gray-800 mb-3">
          {{ illustrationsTitle }}
        </h2>
        <p class="text-gray-600 text-sm leading-relaxed mb-6">
          {{ illustrationsDescription }}
        </p>
        
        <!-- Categories -->
        <div class="grid grid-cols-2 gap-3" v-if="categorys.length">
          <div v-for="(category, index) in categorys"
               :key="index"
               class="bg-gray-50 rounded-lg p-3 flex flex-col relative group active:shadow-lg transition-shadow duration-300"
               :class="{ 'cursor-pointer': category.showMoreInfo }"
               @click="category.showMoreInfo && goToCategoryDetail(category.id)">
            <h3 class="text-sm font-bold text-blue-900 mb-2">{{ category.text }}</h3>
            <p class="text-gray-600 text-xs leading-relaxed flex-1">{{ category.subText }}</p>
            <div v-if="category.showMoreInfo"
                 class="mt-3 text-blue-600 text-xs font-medium flex items-center justify-end gap-1">
              <span>{{ t('sourceProduct.clickForMoreInfo') }}</span>
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Image Gallery Section -->
    <!-- <section class="py-6 bg-white w-full">
      <div class="mx-4">
        <h2 class="text-lg font-bold text-gray-800 mb-4">{{ illustrationsTitle }}</h2>
        <div class="relative" 
             @touchstart="handleTouchStart" 
             @touchmove="handleTouchMove" 
             @touchend="handleTouchEnd">
          <div class="grid grid-cols-2 gap-3 mb-4">
            <div v-for="(item, index) in paginatedImages" :key="index" class="relative">
              <img :src="item.src" :alt="item.alt" 
                   class="w-full object-cover rounded-lg" 
                   style="height: 150px;" />
              <div class="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex items-end justify-start" v-if="item.caption">
                <p class="text-white text-xs text-left font-semibold px-2 pb-2">{{ item.caption }}</p>
              </div>
            </div>
          </div>
          
          <div class="flex justify-center space-x-2 mb-4" v-if="totalPages > 1">
            <div v-for="page in totalPages" :key="page" 
                 class="w-2 h-2 rounded-full"
                 :class="page === currentPage ? 'bg-blue-900' : 'bg-gray-300'">
            </div>
          </div>
        </div>
      </div>
    </section> -->
            <ImageGallery :imageItems="imageItems" imageHeightClass="h-[230px]" :columns="2" />

    <!-- FAQ Section -->
    <section class="py-6 bg-gray-50 w-full" v-if="faqItems.length">
      <div class="mx-4">
        <h2 class="text-lg font-bold text-gray-800 mb-4">{{ faqTitle }}</h2>
        <div class="space-y-3">
          <div v-for="(faq, index) in faqItems" :key="index" class="bg-white rounded-lg p-4 shadow-sm">
            <h3 class="text-sm font-bold text-blue-900 mb-2">{{ faq.question }}</h3>
            <p class="text-gray-600 text-xs leading-relaxed">
              <template v-if="index === 1">
                {{ faq.answer }}
                <router-link to="/core-offers/source-product" class="text-blue-600 font-semibold underline ml-1">{{ faq.linkText1 }}</router-link>
                {{ commonOr }}
                <router-link
                  id="cta-qa2-contactus"
                  to="/contact-us"
                  class="text-blue-600 font-semibold underline ml-1"
                  @click="handleQAContactClick">{{ faq.linkText2 }}</router-link>
                {{ faq.linkSuffix }}
              </template>
              <template v-else-if="index === 6">
                {{ faq.answer }}
                <router-link
                  id="cta-qa7-contactus"
                  to="/contact-us"
                  class="text-blue-600 font-semibold underline"
                  :class="{ 'ml-1': faq.answer }"
                  @click="handleQAContactClick">{{ faq.linkText }}</router-link>
                {{ faq.linkSuffix }}
              </template>
              <template v-else>
                {{ faq.answer }}
              </template>
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import { pushToDataLayer } from '../utils/gtm';
import { useChatWidget } from '../utils/chatWidget';
import { saveCTAInfo, CTA_CONFIG } from '../utils/ctaTracking';
import { imageSources } from "../utils/imageData";
import sourceProduct from "../assets/banner_source_product.png";

const { locale, messages, t } = useI18n();
const router = useRouter();

// 分页相关
const currentPage = ref(1);
const imagesPerPage = 8;

// 触摸事件相关
const touchStartX = ref(0);
const touchEndX = ref(0);

// 计算总页数
const totalPages = computed(() => Math.ceil(imageItems.value.length / imagesPerPage));

// 当前页的图片
const paginatedImages = computed(() => {
  const startIndex = (currentPage.value - 1) * imagesPerPage;
  return imageItems.value.slice(startIndex, startIndex + imagesPerPage);
});

// 翻页函数
const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// 触摸事件处理
const handleTouchStart = (e: TouchEvent) => {
  touchStartX.value = e.touches[0].clientX;
};

const handleTouchMove = (e: TouchEvent) => {
  touchEndX.value = e.touches[0].clientX;
};

const handleTouchEnd = () => {
  const swipeDistance = touchStartX.value - touchEndX.value;
  const minSwipeDistance = 50; // 最小滑动距离

  if (Math.abs(swipeDistance) > minSwipeDistance) {
    if (swipeDistance > 0) {
      // 向左滑动，下一页
      nextPage();
    } else {
      // 向右滑动，上一页
      prevPage();
    }
  }
};

// 其他现有的 computed 属性
const heroTitle = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.hero?.title || 'Sourcing Product';
});

const illustrationsTitle = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.illustrations?.title || 'Sourcing Product Illustrations';
});

const illustrationsDescription = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.illustrations?.description || '';
});

const faqTitle = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.faq?.title || 'Frequently Asked Questions (Q&A)';
});

const imageItems = computed(() => {
  const textItems = messages.value[locale.value]?.sourceProduct?.imageItems || [];
  return imageSources.map((src, index) => {
    const textItem = textItems[index];
    return {
      src,
      alt: textItem?.alt || `Image ${index + 1}`,
      caption: textItem?.caption || ''
    };
  });
});

const categorys = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.categorys || [];
});

const faqItems = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.faq?.items || [];
});

const commonOr = computed(() => {
  return messages.value[locale.value]?.common?.or || 'or';
});

const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("MobileSourceProduct");
  pushToDataLayer("Chat_With_Us", {
    category: "MobileSourceProduct",
    label: "Chat With Us",
  });
};

// Q&A 中的 Contact us 链接点击
const handleQAContactClick = () => {
  saveCTAInfo(CTA_CONFIG.QA_CONTACT);
  pushToDataLayer('click', {
    category: 'MobileSourceProduct',
    label: 'Q&A Contact Us',
  });
};

const goToCategoryDetail = (categoryId: string) => {
  if (categoryId === 'fans') {
    router.push('/core-offers/source-product/fan');
    pushToDataLayer('click', {
      category: 'MobileSourceProduct',
      label: `Category Detail - ${categoryId}`,
    });
  } else if (categoryId === 'filtration-accessories') {
    router.push('/core-offers/source-product/filtration-accessories');
    pushToDataLayer('click', {
      category: 'MobileSourceProduct',
      label: `Category Detail - ${categoryId}`,
    });
  } else if (categoryId === 'belt-conveyor') {
    router.push('/core-offers/source-product/belt-conveyor');
    pushToDataLayer('click', {
      category: 'MobileSourceProduct',
      label: `Category Detail - ${categoryId}`,
    });
  } else if (categoryId === 'flow-components') {
    router.push('/core-offers/source-product/flow-components');
    pushToDataLayer('click', {
      category: 'MobileSourceProduct',
      label: `Category Detail - ${categoryId}`,
    });
  } else if (categoryId === 'silos-tanks') {
    router.push('/core-offers/source-product/silos-tanks');
    pushToDataLayer('click', {
      category: 'MobileSourceProduct',
      label: `Category Detail - ${categoryId}`,
    });
  } else if (categoryId === 'dust-removal') {
    router.push('/core-offers/source-product/dust-removal-system');
    pushToDataLayer('click', {
      category: 'MobileSourceProduct',
      label: `Category Detail - ${categoryId}`,
    });
  } else if (categoryId === 'steel-structures') {
    router.push('/core-offers/source-product/steel-structures');
    pushToDataLayer('click', {
      category: 'MobileSourceProduct',
      label: `Category Detail - ${categoryId}`,
    });
  }
};

onMounted(() => {
  pushToDataLayer('page_view', {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
  });
});
</script>

<style scoped>
.mobile-source-product {
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
}
</style>
